/** Practice statistics derived from stored attempts (pure). */
import type { ExerciseKind } from "./exercises";

export interface PracticeAttempt {
  id: string;
  exercise: ExerciseKind;
  level: number;
  answerId: string; // correct answer
  givenId: string; // what the learner chose
  correct: boolean;
  responseMs: number;
  at: string; // ISO timestamp
}

export interface PracticeSummary {
  attempts: number;
  accuracy: number | null; // 0–1, all time
  recentAccuracy: number | null; // last 20
  medianResponseMs: number | null; // correct answers only
  today: number;
  weakest: { answerId: string; accuracy: number; attempts: number } | null;
}

function median(xs: number[]): number | null {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

/** `now` is injectable for tests; "today" uses the local calendar day. */
export function summarize(attempts: readonly PracticeAttempt[], now = new Date()): PracticeSummary {
  const sorted = [...attempts].sort((a, b) => a.at.localeCompare(b.at));
  const acc = (xs: readonly PracticeAttempt[]) => (xs.length ? xs.filter((x) => x.correct).length / xs.length : null);
  const day = now.toDateString();
  const byAnswer = new Map<string, PracticeAttempt[]>();
  for (const a of sorted) byAnswer.set(a.answerId, [...(byAnswer.get(a.answerId) ?? []), a]);
  let weakest: PracticeSummary["weakest"] = null;
  for (const [answerId, xs] of byAnswer) {
    if (xs.length < 3) continue; // need some evidence before calling something weak
    const a = acc(xs)!;
    if (a < 1 && (!weakest || a < weakest.accuracy)) weakest = { answerId, accuracy: a, attempts: xs.length };
  }
  return {
    attempts: sorted.length,
    accuracy: acc(sorted),
    recentAccuracy: acc(sorted.slice(-20)),
    medianResponseMs: median(sorted.filter((a) => a.correct).map((a) => a.responseMs)),
    today: sorted.filter((a) => new Date(a.at).toDateString() === day).length,
    weakest,
  };
}
